import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react';
import { useSidebarPinState } from '@backstage/core-components';
import { useMediaQuery } from '@material-ui/core';
import { safeStorage } from '../../auth/refreshLatch';

/**
 * The compact menu (mockup « R1 », 2026-10-17): the sidebar as a 72 px rail
 * of icons, which the reader folds and unfolds.
 *
 * The reader's choice, per browser: `on` (always compact), `off` (always
 * expanded), or `auto` — never chosen on this browser, the viewport decides
 * (`AUTO_COMPACT_QUERY`). The tab on the menu's edge, the `[` key, Ctrl+B
 * (Cmd+B on a Mac) and the setting of Paramètres › Général all write the
 * same value.
 */
export type CompactPreference = 'auto' | 'on' | 'off';

export const COMPACT_PREFERENCES: readonly CompactPreference[] = [
  'auto',
  'on',
  'off',
];

/** `on` | `off`; absent for `auto`. */
export const COMPACT_STORAGE_KEY = 'waadoo-nav:compact';

/**
 * The automatic mode's compact viewport: a phone held sideways. Landscape
 * and at most 500 px high — the room a phone in landscape leaves, where 72 px
 * of icons beat 224 px of names — on a touch screen, so that a desktop
 * window merely made short keeps its full menu. Portrait phones (under
 * 600 px wide) never reach this: core-components draws its bottom bar there.
 */
export const AUTO_COMPACT_QUERY =
  '(orientation: landscape) and (max-height: 500px) and (pointer: coarse)';

/**
 * localStorage, or this page's memory where it cannot be used (storage
 * disabled, quota): the guard of the refresh latch (`safeStorage`).
 */
const storage = safeStorage(() => window.localStorage);
const listeners = new Set<() => void>();

function readPreference(): CompactPreference {
  const stored = storage.getItem(COMPACT_STORAGE_KEY);
  return stored === 'on' || stored === 'off' ? stored : 'auto';
}

export function setCompactPreference(preference: CompactPreference): void {
  if (preference === 'auto') {
    storage.removeItem(COMPACT_STORAGE_KEY);
  } else {
    storage.setItem(COMPACT_STORAGE_KEY, preference);
  }
  listeners.forEach(listener => listener());
}

/**
 * This tab's writes, and another tab's through the `storage` event (fired
 * in the OTHER tabs of the same origin only; `key` null is a `clear()`).
 */
function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  const onStorage = (event: StorageEvent) => {
    if (event.key === COMPACT_STORAGE_KEY || event.key === null) {
      onChange();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}

export function useCompactPreference(): CompactPreference {
  return useSyncExternalStore(subscribe, readPreference);
}

/**
 * Whether the menu is compact now, and the way to flip it. Flipping writes
 * the explicit opposite of what is shown: from then on the reader's choice
 * wins over the automatic mode on this browser.
 */
export function useCompactNav(): { compact: boolean; toggle: () => void } {
  const preference = useCompactPreference();
  // Material UI's hook, the one core-components reads its breakpoints with:
  // false where `matchMedia` does not exist (jsdom).
  const automatic = useMediaQuery(AUTO_COMPACT_QUERY, { noSsr: true });
  const compact = preference === 'auto' ? automatic : preference === 'on';
  const toggle = useCallback(
    () => setCompactPreference(compact ? 'off' : 'on'),
    [compact],
  );
  return { compact, toggle };
}

/**
 * Compact is core-components' UNPINNED sidebar with hover expansion
 * disabled (`disableExpandOnHover`, Sidebar.tsx): the drawer stays at
 * `drawerWidthClosed` and `SidebarPage` pads the content by that width, so
 * the page follows and nothing overlaps. Expanded is the pinned one. The pin
 * state lives in `SidebarPage` (its own `useState`, saved to its own
 * localStorage key); this keeps it equal to `!compact`. A layout effect: the
 * correction re-renders before the browser paints, so a reload never shows
 * the other width for a frame. Not on a phone, whose bottom bar ignores the
 * pin.
 */
export function useSidebarPinSync(compact: boolean): void {
  const { isPinned, isMobile, toggleSidebarPinState } = useSidebarPinState();
  useLayoutEffect(() => {
    if (!isMobile && isPinned === compact) {
      toggleSidebarPinState();
    }
  }, [compact, isMobile, isPinned, toggleSidebarPinState]);
}

/** Where a key is text being typed, not a shortcut. */
function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return (
    target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"]), [role="textbox"]',
    ) !== null
  );
}

/**
 * A Mac: Cmd, not Ctrl, is the shortcuts' modifier there. Read from `navigator.platform`, as the
 * terminal does (waadoo-core, terminal/optionKey.ts).
 */
export function isMacPlatform(platform: string | undefined): boolean {
  return /^Mac/.test(platform ?? '');
}

/** The modifier key of Ctrl+B, as the tooltip writes it. */
export function compactShortcutText(mac: boolean): string {
  return mac ? '⌘ B' : 'Ctrl B';
}

/** Ctrl+B or Cmd+B, as `aria-keyshortcuts` names it, then `[`. */
export function compactAriaShortcuts(mac: boolean): string {
  return mac ? 'Meta+B [' : 'Control+B [';
}

/**
 * Whether a keydown is the `[` shortcut. Compared on `key`, the character
 * typed, not on the physical key: on a French AZERTY keyboard `[` is
 * AltGr+5 on Windows and Linux, Option+Shift+( on a Mac, so Alt and Shift
 * are part of typing it and are let through. Ctrl and Meta are not — Cmd+[
 * is the browser's Back on a Mac — except where they are AltGr (Windows
 * reports AltGr as Ctrl+Alt).
 */
function isBracketShortcut(event: KeyboardEvent): boolean {
  if (event.key !== '[') {
    return false;
  }
  return (
    !(event.ctrlKey || event.metaKey) || event.getModifierState('AltGraph')
  );
}

/**
 * Whether a keydown is Ctrl+B — Cmd+B on a Mac, where Ctrl+B stays the
 * text fields' and the terminal's (readline's « back one character »). On
 * `key`, case-insensitive for Caps Lock, as the browsers match their own
 * shortcuts; Shift and Alt excluded, since Ctrl+Shift+B and Cmd+Shift+B
 * are Chrome's bookmarks bar, and Ctrl+Alt is AltGr on Windows.
 */
function isModifierShortcut(event: KeyboardEvent, mac: boolean): boolean {
  if (event.key.toLowerCase() !== 'b' || event.shiftKey || event.altKey) {
    return false;
  }
  return mac
    ? event.metaKey && !event.ctrlKey
    : event.ctrlKey && !event.metaKey;
}

/**
 * Whether a keydown flips the menu: `[` or Ctrl+B (Cmd+B on a Mac), not
 * on repeat, not already handled, and never in a field — the terminal's
 * input included, xterm's hidden `textarea`.
 */
export function isCompactShortcut(event: KeyboardEvent, mac: boolean): boolean {
  if (event.defaultPrevented || event.repeat) {
    return false;
  }
  if (!isBracketShortcut(event) && !isModifierShortcut(event, mac)) {
    return false;
  }
  return !isEditable(event.target);
}

/** `[` or Ctrl+B anywhere in the page, outside a field, flips the menu. */
export function useCompactShortcut(toggle: () => void): void {
  useEffect(() => {
    const mac = isMacPlatform(window.navigator.platform);
    const onKeyDown = (event: KeyboardEvent) => {
      if (isCompactShortcut(event, mac)) {
        event.preventDefault();
        toggle();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [toggle]);
}
